// =============================================================================
// CONFIGURATION CYCLE DE VIE DOSSIER
// Les statuts du dossier (Prospect, En cours, Terminé, etc.)
// =============================================================================

import { useState, useEffect } from "react";
import { 
  Settings, Plus, Trash2, Save, X, GripVertical, 
  ArrowRight, CheckCircle2, Circle, Edit, AlertTriangle,
  Workflow, RotateCw, Clock, Archive
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

// Statuts par défaut du cycle de vie dossier
// NOTE: Utilisé uniquement comme SEED initial. La source de vérité est
// GET /api/metier/config/cycle-vie en DB. Éditable via cette page.
const STATUTS_DEFAULT = [
  { 
    code: "prospect", 
    nom: "Prospect", 
    description: "Premier contact, à qualifier",
    couleur: "bg-zinc-500/20 border border-[var(--oh-border)]", 
    icone: "circle",
    ordre: 1,
    actif: true,
    est_initial: true,
    est_final: false,
    transitions_possibles: ["rdv_planifie", "abandonne"]
  },
  { 
    code: "rdv_planifie", 
    nom: "RDV Planifié", 
    description: "Visite technique programmée",
    couleur: "bg-[var(--oh-accent-solid)]", 
    icone: "calendar",
    ordre: 2,
    actif: true,
    est_initial: false,
    est_final: false,
    transitions_possibles: ["visite_effectuee", "prospect", "abandonne"]
  },
  { 
    code: "visite_effectuee", 
    nom: "Visite Effectuée", 
    description: "Visite réalisée, devis en préparation",
    couleur: "bg-[var(--oh-accent-bg)] border border-[var(--oh-accent)]", 
    icone: "check",
    ordre: 3,
    actif: true,
    est_initial: false,
    est_final: false,
    transitions_possibles: ["devis_envoye", "abandonne"]
  },
  { 
    code: "devis_envoye", 
    nom: "Devis Envoyé", 
    description: "En attente de signature client",
    couleur: "bg-[var(--oh-accent-solid)]", 
    icone: "file",
    ordre: 4,
    actif: true,
    est_initial: false,
    est_final: false,
    transitions_possibles: ["devis_signe", "visite_effectuee", "abandonne"]
  },
  { 
    code: "devis_signe", 
    nom: "Devis Signé", 
    description: "Commande validée",
    couleur: "bg-[var(--oh-accent-bg)] border border-[var(--oh-accent)]", 
    icone: "signature",
    ordre: 5,
    actif: true,
    est_initial: false,
    est_final: false,
    transitions_possibles: ["dossier_complet", "abandonne"]
  },
  { 
    code: "dossier_complet", 
    nom: "Dossier Complet", 
    description: "Tous documents reçus",
    couleur: "bg-cyan-500/15 border border-cyan-500/30", 
    icone: "folder-check",
    ordre: 6,
    actif: true,
    est_initial: false,
    est_final: false,
    transitions_possibles: ["chantier_planifie", "abandonne"]
  },
  { 
    code: "chantier_planifie", 
    nom: "Chantier Planifié", 
    description: "Date d'intervention fixée",
    couleur: "bg-[var(--oh-success-bg)] border border-[var(--oh-success-border)]", 
    icone: "calendar-check",
    ordre: 7,
    actif: true,
    est_initial: false,
    est_final: false,
    transitions_possibles: ["travaux_en_cours", "dossier_complet", "abandonne"]
  },
  { 
    code: "travaux_en_cours", 
    nom: "Travaux en Cours", 
    description: "Installation en cours",
    couleur: "bg-amber-500", 
    icone: "wrench",
    ordre: 8,
    actif: true,
    est_initial: false,
    est_final: false,
    transitions_possibles: ["travaux_termines"]
  },
  { 
    code: "travaux_termines", 
    nom: "Travaux Terminés", 
    description: "Installation finalisée, SAV en cours",
    couleur: "bg-orange-500", 
    icone: "check-circle",
    ordre: 9,
    actif: true,
    est_initial: false,
    est_final: false,
    transitions_possibles: ["dossier_depose"]
  },
  { 
    code: "dossier_depose", 
    nom: "Dossier Déposé", 
    description: "Dossier aides envoyé",
    couleur: "bg-lime-500", 
    icone: "upload",
    ordre: 10,
    actif: true,
    est_initial: false,
    est_final: false,
    transitions_possibles: ["dossier_valide", "dossier_rejete"]
  },
  { 
    code: "dossier_valide", 
    nom: "Dossier Validé", 
    description: "Aides acceptées",
    couleur: "bg-[var(--oh-success-bg)] border border-[var(--oh-success-border)]", 
    icone: "badge-check",
    ordre: 11,
    actif: true,
    est_initial: false,
    est_final: false,
    transitions_possibles: ["dossier_paye"]
  },
  { 
    code: "dossier_paye", 
    nom: "Dossier Payé", 
    description: "Aides encaissées, dossier clôturé",
    couleur: "bg-[var(--oh-info-bg)] border border-[var(--oh-info-border)]", 
    icone: "check-circle-2",
    ordre: 12,
    actif: true,
    est_initial: false,
    est_final: true,
    transitions_possibles: []
  },
  { 
    code: "abandonne", 
    nom: "Abandonné", 
    description: "Dossier annulé ou perdu",
    couleur: "bg-red-500", 
    icone: "x-circle",
    ordre: 99,
    actif: true,
    est_initial: false,
    est_final: true,
    transitions_possibles: []
  },
  { 
    code: "dossier_rejete", 
    nom: "Dossier Rejeté", 
    description: "Aides refusées",
    couleur: "bg-red-400", 
    icone: "alert-circle",
    ordre: 98,
    actif: true,
    est_initial: false,
    est_final: false,
    transitions_possibles: ["dossier_depose", "abandonne"]
  }
];

const COULEURS = [
  { code: "bg-zinc-500/20 border border-[var(--oh-border)]", label: "Gris" },
  { code: "bg-[var(--oh-accent-solid)]", label: "Bleu" },
  { code: "bg-[var(--oh-accent-bg)] border border-[var(--oh-accent)]", label: "Indigo" },
  { code: "bg-[var(--oh-accent-solid)]", label: "Violet" },
  { code: "bg-cyan-500/15 border border-cyan-500/30", label: "Cyan" },
  { code: "bg-[var(--oh-success-bg)] border border-[var(--oh-success-border)]", label: "Teal" },
  { code: "bg-amber-500", label: "Ambre" },
  { code: "bg-orange-500", label: "Orange" },
  { code: "bg-[var(--oh-success-bg)] border border-[var(--oh-success-border)]", label: "Vert" },
  { code: "bg-[var(--oh-info-bg)] border border-[var(--oh-info-border)]", label: "Émeraude" },
  { code: "bg-red-500", label: "Rouge" },
  { code: "bg-pink-500", label: "Rose" },
  { code: "bg-lime-500", label: "Lime" },
];

export default function CycleVieConfig({ tenantId }) {
  const [statuts, setStatuts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [editingStatut, setEditingStatut] = useState(null);

  const getAuthHeaders = () => {
    const token = localStorage.getItem("session_token");
    return {
      "X-Tenant-ID": tenantId || "climatpac",
      "Content-Type": "application/json",
      ...(token && { "Authorization": `Bearer ${token}` })
    };
  };

  useEffect(() => {
    loadConfig();
  }, [tenantId]);

  const loadConfig = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/metier/config/cycle-vie`, {
        headers: getAuthHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        setStatuts(data.statuts?.length > 0 ? data.statuts : STATUTS_DEFAULT);
      } else {
        setStatuts(STATUTS_DEFAULT);
      }
    } catch (err) {
      console.error("Erreur chargement config:", err);
      setStatuts(STATUTS_DEFAULT);
    } finally {
      setLoading(false);
    }
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/metier/config/cycle-vie`, {
        method: "PUT",
        headers: getAuthHeaders(),
        body: JSON.stringify({ statuts })
      });
      if (res.ok) {
        toast.success("Configuration du cycle de vie enregistrée");
        setEditMode(false);
      } else {
        toast.error("Erreur lors de l'enregistrement");
      }
    } catch (err) {
      toast.error("Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const handleAddStatut = () => {
    setEditingStatut({
      code: "",
      nom: "",
      description: "",
      couleur: "bg-zinc-500/20 border border-[var(--oh-border)]",
      ordre: statuts.length + 1,
      actif: true,
      est_initial: false,
      est_final: false,
      transitions_possibles: [],
      isNew: true
    });
    setShowDialog(true);
  };

  const handleEditStatut = (statut) => {
    setEditingStatut({ ...statut });
    setShowDialog(true);
  };

  const handleSaveStatut = () => {
    if (!editingStatut.nom || !editingStatut.code) {
      toast.error("Nom et code obligatoires");
      return;
    }

    if (editingStatut.isNew) {
      setStatuts([...statuts, { ...editingStatut, isNew: undefined }]);
    } else {
      setStatuts(statuts.map(s => 
        s.code === editingStatut.code ? { ...editingStatut } : s
      ));
    }
    setShowDialog(false);
    setEditingStatut(null);
  };

  const handleDeleteStatut = (code) => {
    // Empêcher la suppression des statuts protégés
    const protectedStatuts = ["prospect", "devis_signe", "dossier_paye", "abandonne"];
    if (protectedStatuts.includes(code)) {
      toast.error("Ce statut est protégé et ne peut pas être supprimé");
      return;
    }

    if (window.confirm("Supprimer ce statut ?")) {
      setStatuts(statuts.filter(s => s.code !== code));
    }
  };

  const toggleTransition = (fromCode, toCode) => {
    setStatuts(statuts.map(s => {
      if (s.code === fromCode) {
        const transitions = s.transitions_possibles || [];
        if (transitions.includes(toCode)) {
          return { ...s, transitions_possibles: transitions.filter(t => t !== toCode) };
        } else {
          return { ...s, transitions_possibles: [...transitions, toCode] };
        }
      }
      return s;
    }));
  };

  const statutsActifs = statuts.filter(s => s.actif).sort((a, b) => a.ordre - b.ordre);
  const statutsInactifs = statuts.filter(s => !s.actif);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--oh-accent)]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-medium text-[var(--oh-text)] flex items-center gap-2">
            <Workflow className="w-6 h-6 text-[var(--oh-accent-text)]" />
            Configuration Cycle de Vie
          </h2>
          <p className="text-[var(--oh-text-muted)] text-sm mt-1">
            Les statuts possibles d'un dossier et leurs transitions
          </p>
        </div>
        <div className="flex gap-2">
          {editMode ? (
            <>
              <Button variant="outline" onClick={() => { setEditMode(false); loadConfig(); }}>
                <X className="w-4 h-4 mr-2" />
                Annuler
              </Button>
              <Button onClick={saveConfig} disabled={saving} className="bg-green-600 hover:bg-green-700">
                <Save className="w-4 h-4 mr-2" />
                {saving ? "Enregistrement..." : "Enregistrer"}
              </Button>
            </>
          ) : (
            <Button onClick={() => setEditMode(true)} className="bg-[var(--oh-accent-solid)] hover:bg-[var(--oh-accent-solid)]">
              <Edit className="w-4 h-4 mr-2" />
              Modifier
            </Button>
          )}
        </div>
      </div>

      {/* Vue graphique des statuts */}
      <Card className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]">
        <CardHeader>
          <CardTitle className="text-[var(--oh-text)] text-sm">Flux des statuts</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2 items-center">
            {statutsActifs.map((statut, idx) => (
              <div key={statut.code} className="flex items-center">
                <div className={`px-3 py-2 rounded-lg ${statut.couleur} flex items-center gap-2`}>
                  <span className="text-[var(--oh-text)] text-sm font-medium">{statut.nom}</span>
                  {statut.est_initial && (
                    <Badge className="bg-[var(--oh-bg-surface)]/20 text-[var(--oh-text)] text-xs">Début</Badge>
                  )}
                  {statut.est_final && (
                    <Badge className="bg-[var(--oh-bg-surface)]/20 text-[var(--oh-text)] text-xs">Fin</Badge>
                  )}
                </div>
                {idx < statutsActifs.length - 1 && !statut.est_final && (
                  <ArrowRight className="w-4 h-4 text-[var(--oh-text-secondary)] mx-1 flex-shrink-0" />
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Liste des statuts */}
      <Card className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]">
        <CardHeader className="flex flex-col gap-2 md:flex-row md:gap-0 items-center justify-between">
          <CardTitle className="text-[var(--oh-text)]">Statuts du dossier ({statuts.length})</CardTitle>
          {editMode && (
            <Button size="sm" onClick={handleAddStatut} className="bg-green-600 hover:bg-green-700">
              <Plus className="w-4 h-4 mr-2" />
              Ajouter un statut
            </Button>
          )}
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {statuts.sort((a, b) => a.ordre - b.ordre).map((statut) => (
              <div 
                key={statut.code}
                className={`flex items-center gap-4 p-3 rounded-lg ${
                  statut.actif ? "bg-[var(--oh-bg-surface)]" : "bg-[var(--oh-bg-surface)] opacity-50"
                }`}
              >
                <div className={`w-10 h-10 rounded-lg ${statut.couleur} flex items-center justify-center`}>
                  <span className="text-[var(--oh-text)] font-medium text-sm">{statut.ordre}</span>
                </div>
                
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <p className="text-[var(--oh-text)] font-medium">{statut.nom}</p>
                    {statut.est_initial && (
                      <Badge className="bg-[var(--oh-accent-bg)] text-[var(--oh-accent-text)] text-xs">Initial</Badge>
                    )}
                    {statut.est_final && (
                      <Badge className="bg-[var(--oh-success-bg)] text-[var(--oh-success-text)] text-xs">Final</Badge>
                    )}
                  </div>
                  <p className="text-[var(--oh-text-muted)] text-xs font-mono">{statut.code}</p>
                  {statut.description && (
                    <p className="text-[var(--oh-text-secondary)] text-xs mt-1">{statut.description}</p>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  <Badge className={statut.actif ? "bg-[var(--oh-success-bg)] text-[var(--oh-success-text)]" : "bg-[var(--oh-bg-elevated)]/20 text-[var(--oh-text-muted)]"}>
                    {statut.actif ? "Actif" : "Inactif"}
                  </Badge>

                  {statut.transitions_possibles?.length > 0 && (
                    <Badge className="bg-[var(--oh-accent-bg)] text-[var(--oh-accent-text)]" title={`Vers: ${statut.transitions_possibles.join(", ")}`}>
                      {statut.transitions_possibles.length} transition(s)
                    </Badge>
                  )}
                </div>

                {editMode && (
                  <div className="flex gap-2">
                    <Button size="sm" variant="ghost" onClick={() => handleEditStatut(statut)}>
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button 
                      size="sm" 
                      variant="ghost" 
                      onClick={() => handleDeleteStatut(statut.code)}
                      className="text-[var(--oh-error-text)] hover:text-[var(--oh-error-text)]"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Matrice des transitions */}
      {editMode && (
        <Card className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]">
          <CardHeader>
            <CardTitle className="text-[var(--oh-text)] text-sm flex items-center gap-2">
              <RotateCw className="w-4 h-4 text-[var(--oh-accent-text)]" />
              Matrice des transitions
            </CardTitle>
            <p className="text-[var(--oh-text-muted)] text-xs">
              Cliquez sur une cellule pour activer/désactiver une transition
            </p>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto oh-responsive-table">
              <table className="w-full text-xs">
                <thead>
                  <tr>
                    <th className="p-2 text-left text-[var(--oh-text-muted)]">De \ Vers</th>
                    {statutsActifs.map(s => (
                      <th key={s.code} className="p-2 text-center text-[var(--oh-text-muted)] min-w-[80px]">
                        <div className={`px-2 py-1 rounded ${s.couleur} text-[var(--oh-text)]`}>
                          {s.nom.slice(0, 10)}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {statutsActifs.filter(s => !s.est_final).map(from => (
                    <tr key={from.code} className="border-t border-[var(--oh-border)]">
                      <td className="p-2">
                        <div className={`px-2 py-1 rounded ${from.couleur} text-[var(--oh-text)] inline-block`}>
                          {from.nom.slice(0, 12)}
                        </div>
                      </td>
                      {statutsActifs.map(to => (
                        <td key={to.code} className="p-2 text-center">
                          {from.code !== to.code ? (
                            <button
                              onClick={() => toggleTransition(from.code, to.code)}
                              className={`w-6 h-6 rounded ${
                                from.transitions_possibles?.includes(to.code)
                                  ? "bg-[var(--oh-success-bg)] border border-[var(--oh-success-border)]"
                                  : "bg-[var(--oh-bg-elevated)] hover:bg-slate-600"
                              }`}
                            >
                              {from.transitions_possibles?.includes(to.code) && (
                                <CheckCircle2 className="w-4 h-4 text-[var(--oh-text)] mx-auto" />
                              )}
                            </button>
                          ) : (
                            <span className="text-[var(--oh-text-secondary)]">-</span>
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Dialog édition statut */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)] max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-[var(--oh-text)]">
              {editingStatut?.isNew ? "Ajouter un statut" : "Modifier le statut"}
            </DialogTitle>
          </DialogHeader>
          
          {editingStatut && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-[var(--oh-text-muted)]">Code technique *</Label>
                  <Input
                    value={editingStatut.code}
                    onChange={(e) => setEditingStatut({ 
                      ...editingStatut, 
                      code: e.target.value.toLowerCase().replace(/\s+/g, "_") 
                    })}
                    placeholder="ex: devis_signe"
                    className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)] text-[var(--oh-text)] font-mono"
                    disabled={!editingStatut.isNew}
                  />
                </div>
                <div>
                  <Label className="text-[var(--oh-text-muted)]">Ordre</Label>
                  <Input
                    type="number"
                    value={editingStatut.ordre}
                    onChange={(e) => setEditingStatut({ ...editingStatut, ordre: parseInt(e.target.value) })}
                    className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)] text-[var(--oh-text)]"
                  />
                </div>
              </div>
              
              <div>
                <Label className="text-[var(--oh-text-muted)]">Nom affiché *</Label>
                <Input
                  value={editingStatut.nom}
                  onChange={(e) => setEditingStatut({ ...editingStatut, nom: e.target.value })}
                  placeholder="Ex: Devis Signé"
                  className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)] text-[var(--oh-text)]"
                />
              </div>

              <div>
                <Label className="text-[var(--oh-text-muted)]">Description</Label>
                <Textarea
                  value={editingStatut.description || ""}
                  onChange={(e) => setEditingStatut({ ...editingStatut, description: e.target.value })}
                  placeholder="Description du statut..."
                  className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)] text-[var(--oh-text)]"
                  rows={2}
                />
              </div>

              <div>
                <Label className="text-[var(--oh-text-muted)]">Couleur</Label>
                <Select 
                  value={editingStatut.couleur} 
                  onValueChange={(v) => setEditingStatut({ ...editingStatut, couleur: v })}
                >
                  <SelectTrigger className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)] text-[var(--oh-text)]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {COULEURS.map(c => (
                      <SelectItem key={c.code} value={c.code}>
                        <div className="flex items-center gap-2">
                          <div className={`w-4 h-4 rounded-full ${c.code}`} />
                          {c.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center gap-6">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={editingStatut.actif}
                    onChange={(e) => setEditingStatut({ ...editingStatut, actif: e.target.checked })}
                    className="rounded"
                  />
                  <Label className="text-[var(--oh-text-muted)]">Actif</Label>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={editingStatut.est_initial}
                    onChange={(e) => setEditingStatut({ ...editingStatut, est_initial: e.target.checked })}
                    className="rounded"
                  />
                  <Label className="text-[var(--oh-text-muted)]">Statut initial</Label>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={editingStatut.est_final}
                    onChange={(e) => setEditingStatut({ ...editingStatut, est_final: e.target.checked })}
                    className="rounded"
                  />
                  <Label className="text-[var(--oh-text-muted)]">Statut final</Label>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>
              Annuler
            </Button>
            <Button onClick={handleSaveStatut} className="bg-[var(--oh-accent-solid)] hover:bg-[var(--oh-accent-solid)]">
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
